/**
 * Admin oversight: every claim across the org, regardless of who created or actioned it.
 * Used to answer "X says they submitted but I don't see it" type questions.
 *
 * Bulk actions: pick rows, choose Approve / Mark paid / Delete drafts. The API
 * re-validates per claim so mixed-status selections fail gracefully.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import StatusBadge from "../../components/StatusBadge";
import { gbp, dateShort } from "../../lib/format";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Any status" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted (awaiting manager)" },
  { value: "manager_approved", label: "Manager approved (awaiting payroll)" },
  { value: "payroll_approved", label: "Payroll approved (awaiting payment)" },
  { value: "paid", label: "Paid" },
  { value: "rejected", label: "Rejected" },
];

function todayMinus(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function AllClaimsAdmin() {
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState(todayMinus(60));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [claimant, setClaimant] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<{ ok: number; failed: { id: string; reason: string }[]; action: string } | null>(null);
  const [showRevert, setShowRevert] = useState(false);
  const [revertTo, setRevertTo] = useState<"draft" | "submitted" | "manager_approved" | "payroll_approved">("payroll_approved");
  const [revertReason, setRevertReason] = useState("");
  const qc = useQueryClient();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-all-claims", status, from, to, claimant],
    queryFn: () => api.approvals.allClaims({ status, from, to, claimant }),
  });

  const items = data?.items ?? [];
  const allOnPageIds = useMemo(() => items.map((c: any) => c.id), [items]);
  const allSelected = allOnPageIds.length > 0 && allOnPageIds.every((id: string) => selected.has(id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => {
      if (allOnPageIds.every((id: string) => prev.has(id))) {
        const next = new Set(prev);
        for (const id of allOnPageIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of allOnPageIds) next.add(id);
      return next;
    });
  }

  const bulk = useMutation({
    mutationFn: (args: { action: "approve" | "mark-paid" | "delete" | "revert"; extras?: any }) =>
      api.approvals.bulk(args.action, [...selected], args.extras).then((r) => ({ ...r, action: args.action })),
    onSuccess: (res) => {
      setBulkResult({ ok: res.ok.length, failed: res.failed, action: res.action });
      setSelected(new Set());
      setShowRevert(false);
      setRevertReason("");
      qc.invalidateQueries({ queryKey: ["admin-all-claims"] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
    onError: (e) => setBulkResult({ ok: 0, failed: [{ id: "—", reason: (e as Error).message }], action: "—" }),
  });

  // Decide which bulk actions make sense for the current selection
  const selectedItems = items.filter((c: any) => selected.has(c.id));
  const statuses = new Set(selectedItems.map((c: any) => c.status));
  const allDraft = selectedItems.length > 0 && statuses.size === 1 && statuses.has("draft");
  const canApprove = selectedItems.some((c: any) => c.status === "submitted" || c.status === "manager_approved");
  const canMarkPaid = selectedItems.some((c: any) => c.status === "payroll_approved");
  // Anything not in draft/rejected can be reverted (admin)
  const canRevertAny = selectedItems.some((c: any) => !["draft", "rejected"].includes(c.status));

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">All claims</h1>
        <p className="text-slate-600 mt-1">Cross-org oversight. Every claim by every claimant — drafts, submitted, approved, paid, rejected.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="text-sm">
            <span className="block font-medium text-slate-700 mb-1">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2"
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="block font-medium text-slate-700 mb-1">Created from</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="block font-medium text-slate-700 mb-1">Created to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="block font-medium text-slate-700 mb-1">Claimant (name or email)</span>
            <input
              type="text"
              value={claimant}
              onChange={(e) => setClaimant(e.target.value)}
              placeholder="e.g. amine"
              className="w-full border border-slate-300 rounded-lg px-3 py-2"
            />
          </label>
        </div>
        <div className="flex items-center justify-between mt-3 text-sm">
          <div className="text-slate-500">
            {data && <>Showing <strong>{data.total}</strong> claim{data.total === 1 ? "" : "s"} (max 500)</>}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="text-liaise-navy hover:underline disabled:opacity-50"
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="bg-liaise-navy text-white rounded-xl px-5 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span> selected
            {statuses.size > 1 && <span className="ml-2 text-amber-200">· mixed statuses</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setSelected(new Set())} className="text-white/80 text-sm hover:text-white">
              Clear
            </button>
            {canApprove && (
              <button
                onClick={() => {
                  if (confirm(`Approve ${selected.size} claim${selected.size === 1 ? "" : "s"}? Any in non-approvable status will be skipped.`)) {
                    bulk.mutate({ action: "approve" });
                  }
                }}
                disabled={bulk.isPending}
                className="bg-white text-liaise-navy text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-50"
              >
                Approve
              </button>
            )}
            {canMarkPaid && (
              <button
                onClick={() => {
                  if (confirm(`Mark ${selected.size} claim${selected.size === 1 ? "" : "s"} as paid?`)) {
                    bulk.mutate({ action: "mark-paid" });
                  }
                }}
                disabled={bulk.isPending}
                className="bg-emerald-500 text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-emerald-600 disabled:opacity-50"
              >
                Mark paid
              </button>
            )}
            {allDraft && (
              <button
                onClick={() => {
                  if (confirm(`Delete ${selected.size} draft${selected.size === 1 ? "" : "s"}? This can't be undone.`)) {
                    bulk.mutate({ action: "delete" });
                  }
                }}
                disabled={bulk.isPending}
                className="bg-red-500 text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                Delete drafts
              </button>
            )}
            {canRevertAny && (
              <button
                onClick={() => setShowRevert(true)}
                disabled={bulk.isPending}
                className="bg-amber-500 text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-amber-600 disabled:opacity-50"
              >
                Revert…
              </button>
            )}
          </div>
        </div>
      )}

      {showRevert && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 space-y-3">
          <div className="flex items-baseline justify-between">
            <h3 className="font-medium text-amber-900">Revert {selected.size} claim{selected.size === 1 ? "" : "s"}</h3>
            <button onClick={() => setShowRevert(false)} className="text-xs text-amber-700 hover:underline">Cancel</button>
          </div>
          <p className="text-xs text-amber-800">
            Each claim must already be at a status later than the target. Mismatches will appear in the failure list.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block font-medium text-slate-700 mb-1">Send back to</span>
              <select
                value={revertTo}
                onChange={(e) => setRevertTo(e.target.value as any)}
                className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white"
              >
                <option value="payroll_approved">Payroll approved (re-mark paid)</option>
                <option value="manager_approved">Manager approved (payroll redo)</option>
                <option value="submitted">Submitted (manager redo)</option>
                <option value="draft">Draft (claimant edits)</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block font-medium text-slate-700 mb-1">Reason (sent to each claimant)</span>
              <input
                type="text"
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                placeholder="e.g. Bank details incorrect, BACS bounced."
                className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => bulk.mutate({ action: "revert", extras: { to_status: revertTo, reason: revertReason.trim() } })}
              disabled={!revertReason.trim() || bulk.isPending}
              className="bg-amber-600 text-white font-medium px-4 py-2 rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {bulk.isPending ? "Reverting…" : `Confirm revert (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      {bulkResult && (
        <div className={`rounded-xl px-5 py-3 text-sm border ${bulkResult.failed.length === 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
          <div>
            <strong>{bulkResult.ok}</strong> succeeded ({bulkResult.action})
            {bulkResult.failed.length > 0 && <>, <strong>{bulkResult.failed.length}</strong> failed</>}.
          </div>
          {bulkResult.failed.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs">
              {bulkResult.failed.map((f, i) => (
                <li key={i}>· <code className="font-mono">{f.id.slice(0, 8)}</code> — {f.reason}</li>
              ))}
            </ul>
          )}
          <button onClick={() => setBulkResult(null)} className="text-xs underline mt-2">Dismiss</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {isLoading && <div className="px-6 py-12 text-center text-slate-500">Loading…</div>}
        {error && <div className="px-6 py-12 text-red-600 text-sm">{(error as Error).message}</div>}
        {data && items.length === 0 && (
          <div className="px-6 py-12 text-center text-slate-500">No claims match these filters.</div>
        )}
        {items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                <th className="px-6 py-3 font-medium">Number</th>
                <th className="px-6 py-3 font-medium">Claimant</th>
                <th className="px-6 py-3 font-medium">Cost centre</th>
                <th className="px-6 py-3 font-medium">Approver</th>
                <th className="px-6 py-3 font-medium">Lines</th>
                <th className="px-6 py-3 font-medium text-right">Total</th>
                <th className="px-6 py-3 font-medium">Created</th>
                <th className="px-6 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((c: any) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                  </td>
                  <td className="px-6 py-3">
                    <Link to={`/approvals/${c.id}?stage=manager`} className="text-liaise-navy font-medium hover:underline">
                      {c.request_number}
                    </Link>
                  </td>
                  <td className="px-6 py-3">
                    <Link to={`/admin/claimant/${c.claimant_entra_oid}`} className="font-medium text-slate-700 hover:text-liaise-navy hover:underline">{c.claimant_display_name}</Link>
                    <div className="text-xs text-slate-500">{c.claimant_email}</div>
                  </td>
                  <td className="px-6 py-3 text-slate-600">{c.cost_centre_name}</td>
                  <td className="px-6 py-3 text-slate-600 text-xs">
                    {c.manager_email ?? <span className="text-slate-400 italic">none</span>}
                    {c.manager_overridden && (
                      <span
                        title={`Set by ${c.manager_override_set_by_email ?? "unknown"}${c.manager_override_reason ? `\n\nReason: ${c.manager_override_reason}` : ""}`}
                        className="ml-1 bg-amber-100 text-amber-800 text-[10px] font-medium px-1.5 py-0.5 rounded cursor-help"
                      >
                        ⚠ overridden
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-slate-600">{c.line_count}</td>
                  <td className="px-6 py-3 text-right font-medium">{gbp(Number(c.total_amount))}</td>
                  <td className="px-6 py-3 text-slate-600">{dateShort(c.created_at)}</td>
                  <td className="px-6 py-3"><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
